import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { clientService, Client } from '../../services/client.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusBadge } from '../../components/ui/StatusBadge';

interface ClientForm {
  email: string;
  name: string;
  phone: string;
  address: string;
  company: string;
  tax_id: string;
}

const EMPTY_FORM: ClientForm = {
  email: '',
  name: '',
  phone: '',
  address: '',
  company: '',
  tax_id: '',
};

export function ClientsPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const canCreate = ['sys_admin', 'general_manager', 'receptionist'].includes(
    user?.role ?? ''
  );

  const load = () => {
    setLoading(true);
    setError(null);
    clientService
      .getClients()
      .then(setClients)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.email.toLowerCase().includes(term) ||
        (c.company ?? '').toLowerCase().includes(term)
    );
  }, [clients, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (c: Client) => {
    setEditing(c);
    setForm({
      email: c.email,
      name: c.name,
      phone: c.phone ?? '',
      address: c.address ?? '',
      company: c.company ?? '',
      tax_id: c.tax_id ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) {
      showError('Completa nombre y email');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await clientService.updateClient(editing.id, {
          name: form.name,
          phone: form.phone || undefined,
          address: form.address || undefined,
          company: form.company || undefined,
          tax_id: form.tax_id || undefined,
        });
        showSuccess('Cliente actualizado');
      } else {
        await clientService.createClient({
          email: form.email.trim(),
          name: form.name,
          phone: form.phone || undefined,
          address: form.address || undefined,
          company: form.company || undefined,
          tax_id: form.tax_id || undefined,
        });
        showSuccess('Cliente creado');
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err: any) {
      showError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (c: Client) => {
    if (
      !(await confirm({
        message: `¿${c.status === 'active' ? 'Desactivar' : 'Activar'} a ${c.name}?`,
        danger: c.status === 'active',
      }))
    )
      return;
    try {
      await clientService.toggleClientStatus(c.id);
      showSuccess(c.status === 'active' ? 'Cliente desactivado' : 'Cliente activado');
      load();
    } catch (e: any) {
      showError(e.message || 'Error');
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Clientes</h1>
          <p className="text-xs text-slate-500">{clients.length} registrados</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreate}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
          >
            + Nuevo
          </button>
        )}
      </div>

      <div className="relative">
        <input
          type="search"
          placeholder="Buscar por nombre, email o empresa…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="👥"
          title={clients.length === 0 ? 'Sin clientes' : 'Sin resultados'}
          description={
            clients.length === 0
              ? 'Registra el primer cliente.'
              : 'Prueba con otro término.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {filtered.map((c) => (
            <li key={c.id} className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center flex-shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {c.name}
                    </p>
                    <StatusBadge
                      status={c.status === 'active' ? 'active' : 'inactive'}
                    />
                  </div>
                  <p className="text-xs text-slate-500 truncate">{c.email}</p>
                  {c.company && (
                    <p className="text-[11px] text-slate-400 truncate">
                      {c.company}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                <button
                  onClick={() => openEdit(c)}
                  className="flex-1 py-1.5 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleToggle(c)}
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold ${
                    c.status === 'active'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-emerald-50 text-emerald-600'
                  }`}
                >
                  {c.status === 'active' ? 'Desactivar' : 'Activar'}
                </button>
                {c.phone && (
                  <a
                    href={`tel:${c.phone}`}
                    className="py-1.5 px-3 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold"
                  >
                    📞
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">
                {editing ? 'Editar cliente' : 'Nuevo cliente'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editing}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none disabled:bg-slate-50"
                />
                {editing && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    El email no se puede modificar
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    RFC
                  </label>
                  <input
                    type="text"
                    value={form.tax_id}
                    onChange={(e) =>
                      setForm({ ...form, tax_id: e.target.value.toUpperCase() })
                    }
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Empresa
                </label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : editing ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}